<template>
  <v-dialog v-model="show" max-width="600" persistent>
    <v-toolbar>
      <v-toolbar-title>
        <v-icon>mdi-transcribe</v-icon>
        Assinadores do Documento
      </v-toolbar-title>
    </v-toolbar>
    <v-card>
      <v-card-text>
        <span
          class="grey--text tex--darken-2 font-weight-bold"
        >
          {{ totalSigned }} de {{ signers.length }} {{ signers.length !== 1 ? 'assinaram' : 'assinou' }}
        </span>
        <v-btn
          flat
          color="blue"
          @click="searchMode = true"
          v-if="!searchMode"
        >
          Alterar
        </v-btn>
        <v-autocomplete
          v-model="selectedUser"
          v-if="searchMode"
          :items="usersAvailable"
          item-text="name"
          item-value="id"
          label="Buscar assinadores"
          prepend-inner-icon="search"
          solo
          append-icon="close"
          @click:append="searchMode = false"
          no-data-text="Assinador não encontrado!"
          @input="addSigner"
        >
          <template
            slot="item"
            slot-scope="data"
          >
            <v-list-tile-avatar
              class="mr-1"
              slot="activator"
              v-if="data.item.avatar !== null"
            >
              <img :src="data.item.avatar" />
            </v-list-tile-avatar>
            <v-list-tile-avatar
              class="mr-1"
              color="grey lighten-2"
              v-else
            >
              <span>{{ acronym(data.item.name) }}</span>
            </v-list-tile-avatar>
            <v-list-tile-content>
              <v-list-tile-title v-html="data.item.name"></v-list-tile-title>
              <v-list-tile-sub-title v-html="data.item.role.name"></v-list-tile-sub-title>
            </v-list-tile-content>
          </template>
        </v-autocomplete>

        <!-- List -->
        <section class="signers-list">
          <v-list v-for="signer in signers" :key="signer.id">
            <v-list-tile>
              <v-list-tile-avatar
                class="mr-1"
                size="32"
                v-if="signer.user.avatar"
              >
                <img :src="signer.user.avatar.url"/>
              </v-list-tile-avatar>
              <v-list-tile-avatar
                class="mr-1"
                color="grey lighten-2"
                size="32"
                v-else
              >
                <span>{{ acronym(signer.user.name) }}</span>
              </v-list-tile-avatar>
              <v-list-tile-content>
                <v-list-tile-title v-html="signer.user.name"></v-list-tile-title>
                <v-list-tile-sub-title v-html="signer.user.role.name"></v-list-tile-sub-title>
              </v-list-tile-content>
              <v-list-tile-action>
                <v-layout v-if="signer.signed" align-center>
                  <span class="caption mr-3">
                    {{ format(signer.signed_at, 'DD/MM/YY [às] HH:mm') }}
                  </span>
                  <v-chip
                    class="text-uppercase font-weight-bold black--text"
                    outline
                    small
                  >
                    <v-icon>mdi-check</v-icon>
                    Assinado
                  </v-chip>
                </v-layout>
                <v-layout v-else-if="!signer.signed && !signer.refusal_reason" align-center>
                  <v-chip
                    color="blue darken-1"
                    class="white--text text-uppercase font-weight-bold mr-3"
                    small
                  >
                    Pendente
                  </v-chip>
                  <v-btn
                    class="remove-signer-button"
                    @click="removeSigner(signer)"
                    icon
                    x-small
                    flat
                    color="red darken-2"
                  >
                    <v-icon>mdi-close-circle-outline</v-icon>
                  </v-btn>
                </v-layout>
                <v-layout v-else align-center>

                  <v-tooltip top>

                    <v-chip
                      color="red darken-1"
                      class="white--text text-uppercase font-weight-bold mr-3"
                      small
                      slot="activator"
                    >
                      Recusado
                    </v-chip>
                    <span>{{signer.refusal_reason}}</span>
                  </v-tooltip>

                </v-layout>

              </v-list-tile-action>
            </v-list-tile>
            <v-divider/>
          </v-list>
        </section>
      </v-card-text>
      <v-card-actions>
        <v-layout justify-center>
          <v-btn
            flat
            @click="close"
          >
            Fechar
          </v-btn>
        </v-layout>
      </v-card-actions>
    </v-card>

    <!-- Confirm Remove Signer -->
    <v-dialog
      v-if="signerToRemove.user"
      v-model="showConfirmRemoveSigner"
      width="300"
       persistent
    >
      <v-card>
        <v-card-text>
          <v-layout column justify-center>
            <span>
                Tem certeza que deseja remover o assinador <strong>{{ signerToRemove.user.name }}</strong>?
                <strong>Esta ação não poderá ser desfeita!</strong>
            </span>
          </v-layout>
        </v-card-text>
        <v-card-actions>
          <v-layout justify-center>
            <v-btn @click="showConfirmRemoveSigner = false" flat small>Não</v-btn>
            <v-btn @click="sendRemoveSignerRequest" :loading="loadingRemoveSigner" flat color="error" small>Sim</v-btn>
          </v-layout>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-dialog>
</template>

<script>
import acronym from '@/utils/acronym';
import foldersService from '@/services/FoldersService';
import { pullAllBy, find } from 'lodash';
import format from 'date-fns/format';

export default {
  name: 'AddSignerDialog',
  props: {
    show: {
      type: Boolean,
      default: false,
    },
    doc: {
      type: Object,
      default: () => {},
    },
  },
  data() {
    return {
      acronym,
      format,
      searchMode: false,
      selectedUser: null,
      showConfirmRemoveSigner: false,
      signerToRemove: {},
      loadingRemoveSigner: false,
    };
  },
  mounted() {
    this.getUsers();
  },
  methods: {
    close() {
      this.$emit('toggleAddSignerDialog');
      this.searchMode = false;
    },
    getUsers() {
      this.$store.dispatch('getUsers');
    },
    addSigner() {
      const body = {
        id: this.doc.id,
        user_id: this.selectedUser,
      };

      foldersService.addSigner(body)
        .then(() => {
          this.onRequestSuccess('Assinador adicionado com sucesso');
        })
        .catch((err) => {
          this.onRequestError(err.response.data);
        })
        .finally(() => {
          this.$store.dispatch('getActiveFolder', { id: this.folder.id, loading: false });
        });
    },
    removeSigner(signer) {
      this.signerToRemove = signer;
      this.showConfirmRemoveSigner = true;
    },
    sendRemoveSignerRequest() {
      this.loadingRemoveSigner = true;
      foldersService
        .removeSigner({ id: this.signerToRemove.id })
        .then(() => {
          this.onRequestSuccess('Assinador removido com sucesso');
        })
        .catch((err) => {
          this.onRequestError(err);
        })
        .finally(() => {
          this.loadingRemoveSigner = false;
          this.showConfirmRemoveSigner = false;
          this.signerToRemove = {};
          this.$store.dispatch('getActiveFolder', { id: this.folder.id, loading: false });
        });
    },
    onRequestSuccess(message) {
      this.$store.dispatch('toggleSnackbar', {
        show: true,
        color: 'success',
        message,
        button: { show: true, text: 'Fechar', message: '' },
        timeout: 6000,
      });
      this.selectedUser = null;
    },
    onRequestError(err) {
      this.$store.dispatch('toggleSnackbar', {
        show: true,
        color: 'error',
        message: err[0].signer === undefined ? err[0] : `${err[0].signer} ${err[0].error}`,
        messageIcon: 'mdi-alert-outline',
        button: { show: true, text: 'Fechar', message: '' },
        timeout: 6000,
      });
    },
  },
  computed: {
    totalSigned() {
      let counter = 0;
      this.doc.signers.forEach((item) => {
        if (item.signed) {
          counter += 1;
        }
      });
      return counter;
    },
    signers() {
      const doc = find(this.folder.documents, ['id', this.doc.id]);
      const signers = [...doc.signers];
      return signers;
    },
    usersAvailable() {
      const users = this.$store.getters.users;
      console.log('Users available:', users);
      const signerUsers = this.signers.map(item => item.user);
      const usersAvailable = pullAllBy([...users], [...signerUsers], 'id');
      return usersAvailable;
    },
    folder() {
      return this.$store.getters.activeFolder;
    },
  },
};
</script>

<style lang="scss" scoped>
  .signers-list {
    max-height: 250px;
    overflow-y: auto;
  }

  .remove-signer-button {
    position: absolute !important;
    right: 19px;
    top: 0;
    background: white;
    margin: 0 !important;
    width: 25px;
    height: 24px;

  }
</style>



// WEBPACK FOOTER //
// src/components/document/show/AddSignerDialog.vue